import { css, html, LitElement, unsafeCSS, type TemplateResult } from 'lit';
import mainStyles from './main.scss?inline';
import { property, query, state } from 'lit/decorators.js';
import { LocaleCode } from '../../types/lang.types';
import { OpeningHour, OpeningHours, type SpecialOpeningHour } from './types';
import { DateUtility } from '../calendar/utils/date-utility';

export class TimePicker extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: String, attribute: 'selected-date' })
    selectedDate = '';

    @property({ type: Object })
    openingHours?: OpeningHours;

    @state()
    selectedTimeSlot: string = '';

    @state()
    private _timeSlots: string[] = [];

    @query('input[name="selected-time-slot"]') inputTimeslot!: HTMLInputElement;
    @query('form') formElement!: HTMLFormElement;

    /**
     * Constructs a new TimePicker component instance.
     * Initializes the shadow DOM with open mode and delegates focus.
     */
    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    /**
     * Resets the time picker component to its initial state.
     * Clears the selected time slot and resets the form.
     */
    reset(): void {
        this.selectedTimeSlot = '';
        this.formElement.reset();
    }

    /**
     * Validates the time picker form.
     * @returns True if the form is valid, false otherwise
     */
    validate(): boolean {
        if (this.formElement.checkValidity()) {
            return true;
        }
        const invalidEl = this.formElement.querySelector(':invalid') as HTMLElement | null;
        invalidEl?.scrollIntoView({ block: 'center', behavior: 'auto' });
        window.setTimeout(() => {
            this.formElement.reportValidity();
        }, 300);
        return false;
    }

    /**
     * Handles click events on time slot selectors (@dertour/ui-components emits der-selector-click).
     */
    private _handleSelectorClick(event: CustomEvent): void {
        event.stopPropagation();
        const selector = event.currentTarget as HTMLElement;
        if (selector.tagName.toLowerCase() !== 'der-ui-selector') {
            return;
        }
        this.selectedTimeSlot = selector.textContent?.trim() || '';
    }

    /**
     * Called when component properties or state change.
     * Updates time slots when selectedDate or openingHours change,
     * and handles selected time slot changes.
     * @param changedProperties Map of changed properties
     */
    override updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('selectedTimeSlot')) {
            this._handleSelectedTimeSlotChange();
        }
        if (changedProperties.has('selectedDate') || changedProperties.has('openingHours')) {
            this._updateTimeSlots();
        }
    }

    /**
     * Generates time slots based on opening hours for the selected date.
     * Handles both default opening hours (by weekday) and special opening hours (by specific date).
     */
    private _updateTimeSlots(): void {
        if (!this.selectedDate || !this.openingHours) {
            this._timeSlots = [];
            return;
        }

        // Normalize the selected date to YYYY-MM-DD format for comparison
        const normalizedSelectedDate = this._normalizeDate(this.selectedDate);
        if (!normalizedSelectedDate) {
            console.warn('TimePicker: Could not normalize date:', this.selectedDate);
            this._timeSlots = [];
            return;
        }

        // Check for special opening hours first
        const specialHours = this._getSpecialOpeningHours(normalizedSelectedDate);
        if (specialHours) {
            if (!specialHours.isOpen) {
                this._timeSlots = [];
                return;
            }
            this._timeSlots = this._generateTimeSlotsFromOpeningHours(specialHours);
            return;
        }

        // Use default opening hours based on weekday
        const weekday = this._getWeekdayFromDate(this.selectedDate);

        if (weekday === -1) {
            console.warn('TimePicker: Could not determine weekday from date:', this.selectedDate);
            this._timeSlots = [];
            return;
        }

        const defaultHours = this.openingHours.defaultOpeningHours.find(
            (hour: OpeningHour) => hour.weekday === weekday
        );

        if (!defaultHours) {
            console.warn('TimePicker: No opening hours found for weekday:', weekday, 'Date:', this.selectedDate);
            this._timeSlots = [];
            return;
        }

        const generatedSlots = this._generateTimeSlotsFromOpeningHours(defaultHours);

        if (generatedSlots.length === 0) {
            console.warn('TimePicker: No time slots generated for opening hours:', defaultHours);
        }

        this._timeSlots = generatedSlots;
    }

    /**
     * Gets the weekday (0-6, where 0 is Sunday) from a date string.
     * Handles DD.MM.YYYY format from the calendar component.
     */
    private _getWeekdayFromDate(dateString: string): number {
        if (!dateString) {
            return -1;
        }

        try {
            // Normalize the date string first (handles DD.MM.YYYY format)
            const normalizedDate = this._normalizeDate(dateString);
            if (!normalizedDate || !DateUtility.isValidDateString(normalizedDate)) {
                console.warn('TimePicker: Invalid date string:', dateString);
                return -1;
            }

            const year = DateUtility.getYearAsNumber(normalizedDate);
            const month = DateUtility.getMonthAsNumber(normalizedDate);
            const day = DateUtility.getDayAsNumber(normalizedDate);
            const date = new Date(year, month - 1, day);

            // Validate the date was created correctly
            if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
                console.warn('TimePicker: Date validation failed for:', dateString);
                return -1;
            }

            return date.getDay();
        } catch (error) {
            console.warn('TimePicker: Error getting weekday from date:', dateString, error);
            return -1;
        }
    }

    /**
     * Finds special opening hours for a specific date.
     */
    private _getSpecialOpeningHours(dateString: string): SpecialOpeningHour | null {
        if (!this.openingHours?.specialOpeningHours || !dateString) {
            return null;
        }

        // Normalize dates to YYYY-MM-DD format for comparison
        const selectedDateOnly = this._normalizeDate(dateString);
        if (!selectedDateOnly) {
            return null;
        }

        return (
            this.openingHours.specialOpeningHours.find((special: SpecialOpeningHour) => {
                const specialDateOnly = this._normalizeDate(special.date);
                return specialDateOnly === selectedDateOnly;
            }) || null
        );
    }

    /**
     * Normalizes a date string to YYYY-MM-DD format.
     * Handles multiple formats:
     * - DD.MM.YYYY (e.g., "26.11.2025")
     * - ISO format (2025-12-07T00:00:00Z)
     * - YYYY-MM-DD format
     */
    private _normalizeDate(dateString: string): string | null {
        if (!dateString) {
            return null;
        }

        try {
            // Check if it's DD.MM.YYYY format (e.g., "26.11.2025")
            const ddmmyyyyPattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
            const ddmmyyyyMatch = dateString.match(ddmmyyyyPattern);
            if (ddmmyyyyMatch && ddmmyyyyMatch[1] && ddmmyyyyMatch[2] && ddmmyyyyMatch[3]) {
                const day = ddmmyyyyMatch[1].padStart(2, '0');
                const month = ddmmyyyyMatch[2].padStart(2, '0');
                const year = ddmmyyyyMatch[3];
                const normalized = `${year}-${month}-${day}`;
                if (DateUtility.isValidDateString(normalized)) {
                    return normalized;
                }
                return null;
            }

            // Extract YYYY-MM-DD from ISO format or use as-is if already in YYYY-MM-DD format
            const dateOnly = DateUtility.getDateString(dateString);
            if (DateUtility.isValidDateString(dateOnly)) {
                return dateOnly;
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * Generates time slots from opening hours.
     * Creates 60-minute slots starting every 30 minutes within the opening hours.
     */
    private _generateTimeSlotsFromOpeningHours(hours: OpeningHour | SpecialOpeningHour): string[] {
        const slots: string[] = [];

        // Handle morning hours - ensure both start and end are non-null strings
        if (
            hours.morningStart &&
            hours.morningEnd &&
            typeof hours.morningStart === 'string' &&
            typeof hours.morningEnd === 'string'
        ) {
            const morningSlots = this._generateSlotsForTimeRange(hours.morningStart, hours.morningEnd);
            slots.push(...morningSlots);
        }

        // Handle afternoon hours - ensure both start and end are non-null strings
        if (
            hours.afternoonStart &&
            hours.afternoonEnd &&
            typeof hours.afternoonStart === 'string' &&
            typeof hours.afternoonEnd === 'string'
        ) {
            const afternoonSlots = this._generateSlotsForTimeRange(hours.afternoonStart, hours.afternoonEnd);
            slots.push(...afternoonSlots);
        }

        return slots.sort();
    }

    /**
     * Generates time slots for a time range (e.g., "10:00" to "18:00").
     * Creates 60-minute slots starting every 30 minutes: "10:00 - 11:00", "10:30 - 11:30", etc.
     * Slots that would extend beyond the end time are not included.
     * @param startTime Start time in HH:MM format
     * @param endTime End time in HH:MM format
     * @returns Array of time slot strings in "HH:MM - HH:MM" format
     */
    private _generateSlotsForTimeRange(startTime: string, endTime: string): string[] {
        const slots: string[] = [];
        const start = DateUtility.timeToMinutes(startTime);
        const end = DateUtility.timeToMinutes(endTime);

        if (start === -1 || end === -1 || start >= end) {
            return slots;
        }

        // Generate 60-minute slots starting every 30 minutes
        // Only include slots that don't extend beyond the end time
        let current = start;
        while (current + 60 <= end) {
            const slotStart = DateUtility.minutesToTime(current);
            const slotEnd = DateUtility.minutesToTime(current + 60);
            slots.push(`${slotStart} - ${slotEnd}`);
            current += 30;
        }

        return slots;
    }

    /**
     * Handles changes to the selected time slot.
     * Updates the form input value and dispatches a selection event.
     */
    private _handleSelectedTimeSlotChange(): void {
        if (!this.selectedTimeSlot) {
            return;
        }

        this.inputTimeslot.value = this.selectedTimeSlot;
        /**
         * Event dispatch
         */
        this.dispatchEvent(
            new CustomEvent('der.selected', {
                detail: {
                    selectedSlot: this.selectedTimeSlot,
                    timestamp: new Date().toISOString(),
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Renders the time picker component.
     * Displays available time slots as selectable options.
     * @returns Template result for the time picker component
     */
    override render(): TemplateResult {
        return html`
            <div class="m-time-picker">
                <div class="m-time-picker__time-slots">
                    ${this._timeSlots.map(
                        (slot: string): TemplateResult => html`
                            <der-ui-selector
                                @der-selector-click=${this._handleSelectorClick}
                                ?active=${this.selectedTimeSlot === slot}
                            >
                                ${slot}
                            </der-ui-selector>
                        `
                    )}
                </div>
            </div>
            <form>
                <input type="text" name="selected-time-slot" tabindex="-1" required />
            </form>
        `;
    }
}
